import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Stack,
  IconButton,
  Tooltip,
  Switch,
} from '@mui/material';
import {
  CardGiftcard,
  Refresh as RefreshIcon,
  Business,
  ShoppingCart,
  Redeem,
  AttachMoney,
  Category,
} from '@mui/icons-material';
import adminVouchersService, {
  AdminVoucher,
  AdminVoucherCompra,
  AdminVoucherEmpresa,
  AdminVoucherResgate,
  AdminVoucherTipo,
  VoucherStats,
} from '../../services/vouchers.service';

const fmtCVE = (n: number | null | undefined) =>
  n == null ? '-' : `${Number(n).toLocaleString('pt-PT')} ECV`;
const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '-';

const statusColor = (s: string): 'default' | 'success' | 'warning' | 'error' => {
  switch (s) {
    case 'ativo':
      return 'success';
    case 'rascunho':
      return 'default';
    case 'esgotado':
    case 'parcialmente_usado':
      return 'warning';
    case 'expirado':
    case 'cancelado':
    case 'usado':
      return 'error';
    default:
      return 'default';
  }
};

const PagaliVoucherPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [stats, setStats] = useState<VoucherStats | null>(null);
  const [vouchers, setVouchers] = useState<AdminVoucher[]>([]);
  const [tipos, setTipos] = useState<AdminVoucherTipo[]>([]);
  const [compras, setCompras] = useState<AdminVoucherCompra[]>([]);
  const [resgates, setResgates] = useState<AdminVoucherResgate[]>([]);
  const [empresas, setEmpresas] = useState<AdminVoucherEmpresa[]>([]);
  const [empresaSearch, setEmpresaSearch] = useState('');
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [toggling, setToggling] = useState<string | null>(null);
  // Ignore stale responses when the user types quickly
  const empresaRequestSeq = useRef(0);

  // filters + pagination
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const loadStats = useCallback(async () => {
    try {
      setStats(await adminVouchersService.getStats());
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar estatísticas');
    }
  }, []);

  const loadVouchers = useCallback(async () => {
    try {
      const data = await adminVouchersService.listVouchers({
        search: search || undefined,
        status: statusFilter || undefined,
        limit: 200,
      });
      setVouchers(data);
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar vouchers');
    }
  }, [search, statusFilter]);

  const loadTipos = useCallback(async () => {
    try {
      setTipos(await adminVouchersService.listTipos());
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar tipos');
    }
  }, []);

  const loadCompras = useCallback(async () => {
    try {
      setCompras(await adminVouchersService.listCompras({ limit: 200 }));
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar compras');
    }
  }, []);

  const loadResgates = useCallback(async () => {
    try {
      setResgates(await adminVouchersService.listResgates({ limit: 200 }));
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar resgates');
    }
  }, []);

  /**
   * Load entities with optional search. Uses a sequence counter to drop
   * responses that arrive out of order while the user is typing.
   */
  const loadEmpresas = useCallback(async (searchTerm?: string) => {
    const mySeq = ++empresaRequestSeq.current;
    setLoadingEmpresas(true);
    try {
      const data = await adminVouchersService.listEmpresas(
        searchTerm ? { search: searchTerm } : undefined
      );
      // Discard if a newer request fired in the meantime
      if (mySeq === empresaRequestSeq.current) {
        setEmpresas(data);
      }
    } catch (e: any) {
      if (mySeq === empresaRequestSeq.current) {
        setError(e.message || 'Erro ao carregar empresas');
      }
    } finally {
      if (mySeq === empresaRequestSeq.current) {
        setLoadingEmpresas(false);
      }
    }
  }, []);

  const handleToggleEmpresa = async (empresa: AdminVoucherEmpresa) => {
    const next = !empresa.VOUCHER_ENABLED;
    setToggling(empresa.ID);
    try {
      await adminVouchersService.toggleEmpresa(empresa.ID, next);
      await loadEmpresas(empresaSearch || undefined);
    } catch (e: any) {
      setError(e.message || 'Falha ao alterar estado');
    } finally {
      setToggling(null);
    }
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        loadStats(),
        loadVouchers(),
        loadTipos(),
        loadCompras(),
        loadResgates(),
        loadEmpresas(),
      ]);
    } finally {
      setLoading(false);
    }
  }, [loadStats, loadVouchers, loadTipos, loadCompras, loadResgates, loadEmpresas]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch vouchers list when filters change
  useEffect(() => {
    loadVouchers();
  }, [search, statusFilter, loadVouchers]);

  // Debounced search — wait 300ms after typing stops to fire the request
  useEffect(() => {
    const timer = setTimeout(() => {
      loadEmpresas(empresaSearch || undefined);
    }, 300);
    return () => clearTimeout(timer);
  }, [empresaSearch, loadEmpresas]);

  if (loading && !stats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  // Paginated slice for the current tab
  const paginatedVouchers = vouchers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const paginatedCompras = compras.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const paginatedResgates = resgates.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Stack direction="row" alignItems="center" gap={1}>
            <CardGiftcard color="primary" />
            <Typography variant="h4" fontWeight={600}>
              Pagali Voucher
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Visão global de vouchers na plataforma — empresas, vendas e resgates.
          </Typography>
        </Box>
        <Tooltip title="Recarregar">
          <IconButton onClick={refresh}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          <StatCard
            icon={<CardGiftcard />}
            label="Vouchers"
            value={stats.total_vouchers}
            sub={`${stats.vouchers_ativos} ativos`}
          />
          <StatCard
            icon={<Business />}
            label="Empresas"
            value={stats.total_empresas}
            sub="com vouchers"
          />
          <StatCard
            icon={<ShoppingCart />}
            label="Compras"
            value={stats.total_compras}
          />
          <StatCard
            icon={<AttachMoney />}
            label="Receita"
            value={fmtCVE(stats.receita_total)}
            isString
          />
          <StatCard
            icon={<Redeem />}
            label="Resgates"
            value={stats.total_resgates}
          />
        </Grid>
      )}

      {/* Tabs */}
      <Card>
        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            setPage(0);
          }}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`Empresas (${empresas.length})`} icon={<Business />} iconPosition="start" />
          <Tab label={`Vouchers (${vouchers.length})`} icon={<CardGiftcard />} iconPosition="start" />
          <Tab label={`Compras (${compras.length})`} icon={<ShoppingCart />} iconPosition="start" />
          <Tab label={`Resgates (${resgates.length})`} icon={<Redeem />} iconPosition="start" />
          <Tab label={`Tipos (${tipos.length})`} icon={<Category />} iconPosition="start" />
        </Tabs>

        <CardContent>
          {/* ─────────── Empresas (subscriptions) ─────────── */}
          {tab === 0 && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Apenas empresas com o serviço <strong>ativo</strong> conseguem criar e gerir
                vouchers. Usa o switch para ativar/desativar por empresa.
              </Alert>
              <Stack direction="row" gap={2} mb={2} alignItems="center">
                <TextField
                  size="small"
                  label="Pesquisar empresa"
                  placeholder="nome ou código..."
                  value={empresaSearch}
                  onChange={(e) => setEmpresaSearch(e.target.value)}
                  sx={{ minWidth: 320 }}
                  InputProps={{
                    endAdornment: loadingEmpresas ? (
                      <CircularProgress size={16} />
                    ) : null,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {empresas.length} empresa(s)
                </Typography>
              </Stack>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Empresa</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell align="center">Vouchers</TableCell>
                      <TableCell align="center">Vendas</TableCell>
                      <TableCell>Ativado em</TableCell>
                      <TableCell>Por</TableCell>
                      <TableCell align="center">Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {empresas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          Nenhuma empresa encontrada.
                        </TableCell>
                      </TableRow>
                    ) : (
                      empresas.map((emp) => (
                        <TableRow key={emp.ID} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {emp.DESIG}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {emp.CODIGO}
                              {emp.EMAIL ? ` · ${emp.EMAIL}` : ''}
                            </Typography>
                          </TableCell>
                          <TableCell>{emp.TIPO_ENTIDADE || '-'}</TableCell>
                          <TableCell align="center">{emp.total_vouchers}</TableCell>
                          <TableCell align="center">{emp.total_vendas}</TableCell>
                          <TableCell>{fmtDate(emp.VOUCHER_ENABLED_AT)}</TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {emp.VOUCHER_ENABLED_BY || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                              <Switch
                                checked={emp.VOUCHER_ENABLED === 1}
                                disabled={toggling === emp.ID}
                                onChange={() => handleToggleEmpresa(emp)}
                                color="primary"
                              />
                              {toggling === emp.ID && <CircularProgress size={14} />}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* ─────────── Vouchers ─────────── */}
          {tab === 1 && (
            <>
              <Stack direction="row" gap={2} mb={2}>
                <TextField
                  size="small"
                  label="Pesquisar (título, código, empresa)"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ minWidth: 320 }}
                />
                <TextField
                  size="small"
                  select
                  label="Estado"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="rascunho">Rascunho</MenuItem>
                  <MenuItem value="ativo">Ativo</MenuItem>
                  <MenuItem value="esgotado">Esgotado</MenuItem>
                  <MenuItem value="expirado">Expirado</MenuItem>
                  <MenuItem value="cancelado">Cancelado</MenuItem>
                </TextField>
              </Stack>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Título / Código</TableCell>
                      <TableCell>Empresa</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Valor / Desc.</TableCell>
                      <TableCell align="right">Preço</TableCell>
                      <TableCell align="center">Stock</TableCell>
                      <TableCell align="right">Receita</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Criado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedVouchers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          Nenhum voucher encontrado.
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedVouchers.map((v) => (
                        <TableRow key={v.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {v.titulo}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {v.codigo}
                            </Typography>
                          </TableCell>
                          <TableCell>{v.empresa_nome || '-'}</TableCell>
                          <TableCell>
                            <Chip label={v.tipo_nome} size="small" />
                          </TableCell>
                          <TableCell>
                            {v.tipo_categoria === 'valor'
                              ? fmtCVE(v.valor)
                              : v.percentagem_desconto != null
                              ? `${v.percentagem_desconto}% OFF`
                              : '-'}
                          </TableCell>
                          <TableCell align="right">{fmtCVE(v.preco_venda)}</TableCell>
                          <TableCell align="center">
                            {v.quantidade_vendida}/{v.quantidade_total}
                          </TableCell>
                          <TableCell align="right">{fmtCVE(v.receita_total)}</TableCell>
                          <TableCell>
                            <Chip label={v.status} size="small" color={statusColor(v.status)} />
                          </TableCell>
                          <TableCell>{fmtDate(v.created_at)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={vouchers.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}

          {/* ─────────── Compras ─────────── */}
          {tab === 2 && (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Código Único</TableCell>
                      <TableCell>Voucher</TableCell>
                      <TableCell>Empresa</TableCell>
                      <TableCell>Comprador</TableCell>
                      <TableCell align="right">Pago</TableCell>
                      <TableCell align="right">Saldo</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Comprado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedCompras.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          Nenhuma compra registada.
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedCompras.map((c) => (
                        <TableRow key={c.id} hover>
                          <TableCell>
                            <Typography variant="caption" fontFamily="monospace">
                              {c.codigo_unico}
                            </Typography>
                          </TableCell>
                          <TableCell>{c.voucher_titulo}</TableCell>
                          <TableCell>{c.empresa_nome || '-'}</TableCell>
                          <TableCell>
                            <Typography variant="body2">{c.comprador_nome || c.beneficiario_nome || '-'}</Typography>
                            {c.comprador_email && (
                              <Typography variant="caption" color="text.secondary">
                                {c.comprador_email}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{fmtCVE(c.preco_pago)}</TableCell>
                          <TableCell align="right">{fmtCVE(c.valor_restante)}</TableCell>
                          <TableCell>
                            <Chip label={c.status} size="small" color={statusColor(c.status)} />
                          </TableCell>
                          <TableCell>{fmtDate(c.data_compra)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={compras.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}

          {/* ─────────── Resgates ─────────── */}
          {tab === 3 && (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Voucher</TableCell>
                      <TableCell>Empresa</TableCell>
                      <TableCell>Código</TableCell>
                      <TableCell align="right">Valor / Desc.</TableCell>
                      <TableCell>Método</TableCell>
                      <TableCell>Operador</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedResgates.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          Nenhum resgate registado.
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedResgates.map((r) => (
                        <TableRow key={r.id} hover>
                          <TableCell>{fmtDate(r.created_at)}</TableCell>
                          <TableCell>{r.voucher_titulo}</TableCell>
                          <TableCell>{r.empresa_nome || '-'}</TableCell>
                          <TableCell>
                            <Typography variant="caption" fontFamily="monospace">
                              {r.codigo_unico}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {r.valor_resgatado != null
                              ? fmtCVE(r.valor_resgatado)
                              : r.desconto_aplicado != null
                              ? `${r.desconto_aplicado}%`
                              : '-'}
                          </TableCell>
                          <TableCell>
                            <Chip label={r.metodo_validacao} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>{r.operador_nome || '-'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={resgates.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}

          {/* ─────────── Tipos ─────────── */}
          {tab === 4 && (
            <Grid container spacing={2}>
              {tipos.length === 0 ? (
                <Grid size={{ xs: 12 }}>
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    Nenhum tipo de voucher definido.
                  </Typography>
                </Grid>
              ) : (
                tipos.map((t) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={t.id}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        backgroundColor: t.cor_fundo || undefined,
                        color: t.cor_texto || undefined,
                      }}
                    >
                      <Typography variant="h6">{t.nome}</Typography>
                      <Typography variant="caption">Tipo: {t.tipo}</Typography>
                      <Box mt={1}>
                        <Chip label={t.status} size="small" color={t.status === 'ativo' ? 'success' : 'default'} />
                      </Box>
                    </Paper>
                  </Grid>
                ))
              )}
            </Grid>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number | string;
  sub?: string;
  isString?: boolean;
}> = ({ icon, label, value, sub, isString }) => (
  <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" alignItems="center" gap={1} mb={1}>
          {icon}
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        </Stack>
        <Typography variant={isString ? 'h6' : 'h4'} fontWeight={600}>
          {value}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  </Grid>
);

export default PagaliVoucherPage;
